import { Box, FormControl, Text, VStack } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { getRoleDefaultSelectionsForTaxonomy } from '../../../next/CustomDash/filters/utils/getRoleDefaultSelectionsForTaxonomy';
import RoleTaxonomyIdSelect from '../../../shared/components/RoleTaxonomyIdSelect';
import SkillTaxonomyIdSelect from '../../../shared/components/SkillTaxonomyIdSelect';
import { useSelectionLists } from '../../../shared/selection-lists/hooks/useSelectionLists';
import {
  resolveAdaptiveLabelFromId,
  resolveAdaptiveLabelFromSelection,
} from '../../components/utils/roleTaxonomy';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import {
  PreferenceScope,
  UserPreferenceKey,
  UserPreferenceSubKey,
  type UserPreferences,
} from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import { mapTaxonomyIdToSelection } from '../utils/taxonomy-mapper';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.Taxonomies,
  label: 'Taxonomies',
};

export const isAdaptiveSupported = (scope: PreferenceScope) =>
  scope === PreferenceScope.COMPANY;

export default function TaxonomiesEditor({
  affectedScope,
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const selectionLists = useSelectionLists(['rics_k50', 'rics_k10']);
  const roleTaxonomyPref = preferences[UserPreferenceKey.Taxonomies]?.role;
  const skillTaxonomyPref = preferences[UserPreferenceKey.Taxonomies]?.skill;

  const {
    roleTaxonomyId,
    roleShowAdaptive,
    roleAdaptiveResolvedLabel,
    roleIsAdaptive,
  } = useMemo(() => {
    const show = isAdaptiveSupported(affectedScope);

    // If adaptive is visible and explicitly selected in preferences, show 'adaptive'
    if (show && roleTaxonomyPref === 'adaptive') {
      const resolvedLabel = resolveAdaptiveLabelFromSelection(
        selectionLists ?? {},
        preferences[UserPreferenceKey.Taxonomies]
          ?.effectiveRoleTaxonomySelection
      );
      return {
        roleTaxonomyId: undefined,
        roleShowAdaptive: show,
        roleAdaptiveResolvedLabel: resolvedLabel,
        roleIsAdaptive: true,
      };
    }

    // Prefer the explicit preference value for display
    const source:
      | CustomRoleTaxonomySelection
      | { default: true }
      | 'adaptive'
      | undefined = roleTaxonomyPref;

    // Map explicit default
    if (
      typeof source === 'object' &&
      (source as { default?: boolean })?.default
    ) {
      return {
        roleTaxonomyId: 0,
        roleShowAdaptive: show,
        roleAdaptiveResolvedLabel: undefined,
        roleIsAdaptive: false,
      };
    }

    // Non-adaptive concrete selection
    let id: number | undefined;
    const src = source as CustomRoleTaxonomySelection | undefined;
    if (src?.taxonomyId) {
      id = src.taxonomyId;
    } else if (src?.ricsK50Id) {
      id = parseInt(src.ricsK50Id as string, 10);
    } else if (src?.ricsK10Id) {
      id = parseInt(src.ricsK10Id as string, 10) + 50; // mapped into 51-60 UI range
    } else {
      id = undefined;
    }
    return {
      roleTaxonomyId: id,
      roleShowAdaptive: show,
      roleAdaptiveResolvedLabel: undefined,
      roleIsAdaptive: false,
    };
  }, [roleTaxonomyPref, affectedScope, selectionLists, preferences]);

  const {
    skillTaxonomyId,
    skillShowAdaptive,
    skillAdaptiveResolvedLabel,
    skillIsAdaptive,
  } = useMemo(() => {
    const show = isAdaptiveSupported(affectedScope);

    // If adaptive is visible and explicitly selected in preferences, show 'adaptive'
    if (show && skillTaxonomyPref === 'adaptive') {
      const resolvedLabel = resolveAdaptiveLabelFromId(
        selectionLists ?? {},
        preferences[UserPreferenceKey.Taxonomies]
          ?.effectiveSkillTaxonomySelection
      );
      return {
        skillTaxonomyId: undefined,
        skillShowAdaptive: show,
        skillAdaptiveResolvedLabel: resolvedLabel,
        skillIsAdaptive: true,
      };
    }

    // Prefer the explicit preference value for display
    const source: number | 'adaptive' | undefined = skillTaxonomyPref;

    if (typeof source === 'number') {
      return {
        skillTaxonomyId: source,
        skillShowAdaptive: show,
        skillAdaptiveResolvedLabel: undefined,
        skillIsAdaptive: false,
      };
    }

    return {
      skillTaxonomyId: undefined,
      skillShowAdaptive: show,
      skillAdaptiveResolvedLabel: undefined,
      skillIsAdaptive: false,
    };
  }, [skillTaxonomyPref, affectedScope, selectionLists, preferences]);

  const updateRoleTaxonomyPreference = useCallback(
    (role: 'adaptive' | CustomRoleTaxonomySelection | { default: true }) => {
      const updated = {
        ...(preferences[UserPreferenceKey.Taxonomies] || {}),
        role,
      };
      const typedUpdate =
        updated as UserPreferences[UserPreferenceKey.Taxonomies];

      PreferenceBrowserStorage.update(
        affectedScope,
        UserPreferenceKey.Taxonomies,
        typedUpdate
      );

      // If we're updating role taxonomy in ROLE scope, also update ROLE default filters
      // to the generated defaults for that taxonomy (overwriting any previously saved role defaults).
      if (affectedScope === PreferenceScope.ROLE) {
        const selectionForDefaults: CustomRoleTaxonomySelection =
          role === 'adaptive'
            ? { default: true }
            : (role as CustomRoleTaxonomySelection);

        const nextRoleDefaults =
          getRoleDefaultSelectionsForTaxonomy(selectionForDefaults);

        PreferenceBrowserStorage.update(
          affectedScope,
          UserPreferenceKey.DefaultFilters,
          nextRoleDefaults
        );
      }

      // Dispatch after storage updates so taxonomy-dependent filter transforms can
      // read the latest preference values from session storage.
      onPreferenceUpdate?.(
        UserPreferenceKey.Taxonomies,
        typedUpdate,
        UserPreferenceSubKey.RoleTaxonomy
      );
    },
    [affectedScope, onPreferenceUpdate, preferences]
  );

  const updateSkillTaxonomyPreference = useCallback(
    (skill: 'adaptive' | number | { default: true }) => {
      const updated = {
        ...(preferences[UserPreferenceKey.Taxonomies] || {}),
        skill,
      } as UserPreferences[UserPreferenceKey.Taxonomies];

      onPreferenceUpdate?.(
        UserPreferenceKey.Taxonomies,
        updated,
        UserPreferenceSubKey.SkillTaxonomy
      );
      PreferenceBrowserStorage.update(
        affectedScope,
        UserPreferenceKey.Taxonomies,
        updated
      );
    },
    [affectedScope, onPreferenceUpdate, preferences]
  );

  const handleRoleChange = useCallback(
    (newTaxonomyId: number | 'adaptive') => {
      // Map id into v3 selection object (0 means explicit default selection)
      if (newTaxonomyId === 0) {
        updateRoleTaxonomyPreference({ default: true });
        return;
      } else if (newTaxonomyId === 'adaptive') {
        updateRoleTaxonomyPreference('adaptive');
        return;
      }

      const selection = mapTaxonomyIdToSelection(newTaxonomyId);
      updateRoleTaxonomyPreference(selection);
    },
    [updateRoleTaxonomyPreference]
  );

  const handleSkillChange = useCallback(
    (newTaxonomyId: number | 'adaptive') => {
      if (newTaxonomyId === 'adaptive') {
        updateSkillTaxonomyPreference('adaptive');
        return;
      }
      if (Number.isNaN(newTaxonomyId)) newTaxonomyId = 0;
      updateSkillTaxonomyPreference(newTaxonomyId);
    },
    [updateSkillTaxonomyPreference]
  );

  return (
    <VStack
      align="stretch"
      spacing={3}
      width="100%"
      data-testid="taxonomies-editor"
    >
      <FormControl>
        <PreferenceEditorSectionHeading
          title="Role Taxonomy"
          indicateDiff={
            diff?.includes(UserPreferenceSubKey.RoleTaxonomy) ?? false
          }
        />
        <Box data-testid="role-taxonomy-id-select" mb={2}>
          <RoleTaxonomyIdSelect
            value={roleIsAdaptive ? 'adaptive' : roleTaxonomyId}
            includeAdaptive={roleShowAdaptive}
            currentResolvedAdaptiveLabel={roleAdaptiveResolvedLabel}
            variant="default"
            usePortal={false}
            includeCustom={true}
            onChange={handleRoleChange}
          />
        </Box>
        <Text fontSize="xs" color="gray.500" lineHeight="1.4">
          Choose how job roles are categorized. <strong>Adaptive</strong> uses
          our proprietary clustering to group similar job titles across
          companies and industries. <strong>Generic</strong> provides standard
          role categories. <strong>Custom</strong> taxonomies are tailored to
          specific company naming conventions.
        </Text>
      </FormControl>
      <FormControl>
        <PreferenceEditorSectionHeading
          title="Skill Taxonomy"
          indicateDiff={
            diff?.includes(UserPreferenceSubKey.SkillTaxonomy) ?? false
          }
        />
        <Box data-testid="skill-taxonomy-id-select" mb={2}>
          <SkillTaxonomyIdSelect
            value={skillIsAdaptive ? 'adaptive' : skillTaxonomyId}
            includeAdaptive={skillShowAdaptive}
            currentResolvedAdaptiveLabel={skillAdaptiveResolvedLabel}
            variant="default"
            usePortal={false}
            includeCustom={true}
            onChange={handleSkillChange}
          />
        </Box>
        <Text fontSize="xs" color="gray.500" lineHeight="1.4">
          Select how skills are organized. <strong>Adaptive</strong> groups
          related skills using our clustering algorithm to create universal
          skill categories. <strong>Generic</strong> offers standard skill
          groupings. <strong>Custom</strong> taxonomies reflect company-specific
          skill frameworks and terminology.
        </Text>
      </FormControl>
    </VStack>
  );
}
